import { useState } from "react";
import { motion } from "framer-motion";
import { Shield, HeartPulse, Package, Sparkles, Check, ArrowRight, Volume2, Lightbulb, Tablet, Box } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/shared/PageHero";
import CTASection from "@/components/shared/CTASection";
import { productCategories, useCasePackages, getProductLinesByUseCase, getCustomizedProducts } from "@/data/productLines";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Shield,
  HeartPulse,
  Package,
  Sparkles,
  Volume2,
  Lightbulb,
  Tablet,
  Box,
};

const ProductCustomization = () => {
  const [activeCategory, setActiveCategory] = useState(productCategories[0].id);

  const activeCategoryData = productCategories.find((cat) => cat.id === activeCategory);
  const applicableProducts = getProductLinesByUseCase(activeCategory);
  const packages = useCasePackages.filter((pkg) => pkg.useCase === activeCategory);
  const customizedProducts = getCustomizedProducts();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <PageHero
        title="Customize Your Fleet"
        subtitle="Purpose-built mobility solutions tailored to your industry needs"
        variant="vibrant"
      />

      {/* Customized Products Showcase */}
      {customizedProducts.length > 0 && (
        <section className="py-16 lg:py-24 bg-background">
          <div className="container mx-auto px-6">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Our Customized Vehicles
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Purpose-built vehicles fully equipped and ready for deployment
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {customizedProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="group hover:border-primary/50 transition-colors overflow-hidden">
                    <div className="aspect-[3/2] overflow-hidden bg-muted">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        {product.name}
                        <Badge variant="default" className="bg-primary/90">{product.tagline}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4">{product.description}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {product.variants[0]?.specs.slice(0, 4).map((spec) => (
                          <span key={spec.label} className="inline-flex items-center gap-1 px-2.5 py-1 bg-muted rounded-full text-xs">
                            <span className="text-muted-foreground">{spec.label}:</span>
                            <span className="font-medium text-foreground">{spec.value}</span>
                          </span>
                        ))}
                      </div>
                      <Button variant="outline" className="w-full" asChild>
                        <Link to={`/products/${product.id}`}>
                          View Details
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Use Case Categories */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Choose Your Industry
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Select your use case to explore specialized equipment packages and compatible vehicles
            </p>
          </motion.div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {productCategories.slice(0, 4).map((category) => {
              const IconComponent = iconMap[category.icon] || Shield;
              const isActive = activeCategory === category.id;

              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={cn(
                    "flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all",
                    isActive
                      ? "bg-primary text-primary-foreground shadow-lg"
                      : "bg-muted hover:bg-muted/80 text-foreground border border-border"
                  )}
                >
                  <IconComponent className="w-5 h-5" />
                  {category.name}
                </button>
              );
            })}
          </div>

          {/* Active Category Content */}
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Description */}
            <div className="text-center mb-12">
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {activeCategoryData?.description}
              </p>
            </div>

            {/* Compatible Vehicles */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
                Compatible Vehicles
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {applicableProducts.map((product) => (
                  <Card key={product.id} className="group hover:border-primary/50 transition-colors">
                    <CardHeader className="pb-4">
                      <div className="aspect-[3/2] rounded-lg overflow-hidden bg-muted mb-4">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <CardTitle className="flex items-center justify-between">
                        {product.name}
                        <Badge variant="secondary">{product.brand}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4">{product.tagline}</p>
                      <div className="flex flex-wrap gap-2">
                        {product.variants.map((variant) => (
                          <Badge key={variant.id} variant="outline" className="text-xs">
                            {variant.name}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Equipment Packages */}
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
                Equipment Packages
              </h3>
              <div className="grid lg:grid-cols-2 gap-8">
                {packages.map((pkg) => (
                  <Card key={pkg.id} className="overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-primary/10 to-transparent">
                      <CardTitle className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <Package className="w-5 h-5 text-primary" />
                        </div>
                        {pkg.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        {pkg.equipment.map((item) => (
                          <div key={item.name} className="flex items-start gap-3">
                            <div className="w-6 h-6 rounded-full bg-eco-green/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-4 h-4 text-eco-green" />
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{item.name}</p>
                              <p className="text-sm text-muted-foreground">{item.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Additional Specs */}
                      {pkg.additionalSpecs.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-border">
                          <p className="text-sm font-medium text-muted-foreground mb-3">
                            Technical Specifications
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {pkg.additionalSpecs.map((spec) => (
                              <span
                                key={spec.label}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-full text-sm"
                              >
                                <span className="text-muted-foreground">{spec.label}:</span>
                                <span className="font-medium text-foreground">{spec.value}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Additional Equipment List */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Available Accessories
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Additional equipment and accessories to enhance your fleet
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: "PA Systems", description: "100W - 200W speaker systems with siren", icon: Volume2 },
              { name: "LED Lighting", description: "Emergency lights, strobes, and beacons", icon: Lightbulb },
              { name: "Tablet Mounts", description: "RAM mounts for rugged tablets", icon: Tablet },
              { name: "Storage Solutions", description: "Cargo boxes and secure compartments", icon: Box },
            ].map((accessory) => (
              <Card key={accessory.name} className="text-center hover:border-primary/50 transition-colors">
                <CardContent className="pt-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <accessory.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">{accessory.name}</h4>
                  <p className="text-sm text-muted-foreground">{accessory.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button size="lg" asChild>
              <Link to="/contact?inquiry=customization">
                Request Custom Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to Build Your Custom Fleet?"
        description="Our team will work with you to design the perfect mobility solution for your specific requirements."
        primaryButtonText="Contact Our Team"
        primaryButtonLink="/contact?inquiry=customization"
        secondaryButtonText="View Standard Models"
        secondaryButtonLink="/products"
      />

      <Footer />
    </div>
  );
};

export default ProductCustomization;
